/**
 * Litigations sub-page — firm-wide list of all litigations (across every
 * task) shown as the "Litigations" tab on the Tasks page, alongside
 * Sub-Tasks / Certificates / Compliances. Each row links to the parent
 * task's detail view where the inline Litigations tab handles editing,
 * adding steps and raising invoices.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listAllLitigations, getStaff } from '../api';
import { showToast } from '../utils/toast';
import { formatDateDdMmmYy, formatRupeesUI } from '../utils/format';

// ─── Types ────────────────────────────────────────────────────────────────

type LitStatus = 'OPEN' | 'CLOSED' | 'WON' | 'LOST' | 'PARTIALLY_WON' | 'SETTLED' | 'WITHDRAWN';
type Stage = 'ASSESSMENT' | 'FIRST_APPEAL' | 'TRIBUNAL' | 'HIGH_COURT' | 'SUPREME_COURT' | 'OTHER';
type StepType = 'REPLY' | 'HEARING' | 'APPEAL' | 'ORDER_RECEIVED' | 'NOTICE_RECEIVED' | 'OTHER';
type StepStatus = 'PENDING' | 'COMPLETED' | 'ADJOURNED' | 'SKIPPED';
type PausedReason = 'HEARING_AWAITED' | 'ORDER_AWAITED' | 'STAY_GRANTED' | 'OTHER';

interface Step {
  id: number;
  type: StepType;
  title: string;
  dueDate: string | null;
  hearingDate: string | null;
  status: StepStatus;
  stage: Stage | null;
  billableAmount: number | string | null;
  invoice: { id: number; invoiceNumber: string; status: string } | null;
  documents: Array<{ id: number }>;
}

interface Litigation {
  id: number;
  litigationNumber: string;
  title: string;
  caseNumber: string | null;
  forum: string | null;
  opposingParty: string | null;
  status: LitStatus;
  startedOn: string | null;
  closedOn: string | null;
  currentStage: Stage | null;
  lawArea: 'INCOME_TAX' | 'GST' | 'OTHER' | null;
  responsible: { id: number; staffName: string } | null;
  responsibleId: number | null;
  pausedReason: PausedReason | null;
  pausedOn: string | null;
  pausedNote: string | null;
  resumeOn: string | null;
  limitationDate: string | null;
  limitationBasis: string | null;
  disputedAmount: number | string | null;
  preDepositPaid: number | string | null;
  preDepositRef: string | null;
  hasNextAction: boolean;
  lastActivityAt: string | null;
  fees: {
    quoted: number;
    billed: number;
    collected: number;
    outstandingAR: number;
    unbilledQuote: number;
    unbilledCompleted: number;
  };
  steps: Step[];
  invoices: Array<{ id: number; invoiceNumber: string; status: string }>;
  documents: Array<{ id: number }>;
  task: {
    id: number;
    taskId: string;
    taskName: string;
    client: { id: number; clientCode: string; clientName: string } | null;
  };
}

interface StaffLite { id: number; staffName: string; isActive?: boolean }

const STATUS_OPTIONS: ('ALL' | LitStatus)[] = ['ALL','OPEN','CLOSED','WON','LOST','PARTIALLY_WON','SETTLED','WITHDRAWN'];
const STAGE_OPTIONS: ('ALL' | Stage)[] = ['ALL','ASSESSMENT','FIRST_APPEAL','TRIBUNAL','HIGH_COURT','SUPREME_COURT','OTHER'];
const LAW_AREA_OPTIONS: ('ALL' | 'INCOME_TAX' | 'GST' | 'OTHER')[] = ['ALL','INCOME_TAX','GST','OTHER'];
const CLOSED_STATUSES: LitStatus[] = ['CLOSED','WON','LOST','PARTIALLY_WON','SETTLED','WITHDRAWN'];

const STATUS_BADGE: Record<LitStatus, string> = {
  OPEN:          'bg-blue-100 text-blue-700 border-blue-200',
  CLOSED:        'bg-gray-100 text-gray-700 border-gray-200',
  WON:           'bg-emerald-100 text-emerald-700 border-emerald-200',
  PARTIALLY_WON: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  LOST:          'bg-red-100 text-red-700 border-red-200',
  SETTLED:       'bg-indigo-100 text-indigo-700 border-indigo-200',
  WITHDRAWN:     'bg-amber-100 text-amber-700 border-amber-200',
};

const STAGE_BADGE: Record<Stage, string> = {
  ASSESSMENT:    'bg-orange-100 text-orange-700 border-orange-200',
  FIRST_APPEAL:  'bg-blue-100 text-blue-700 border-blue-200',
  TRIBUNAL:      'bg-purple-100 text-purple-700 border-purple-200',
  HIGH_COURT:    'bg-indigo-100 text-indigo-700 border-indigo-200',
  SUPREME_COURT: 'bg-red-100 text-red-700 border-red-200',
  OTHER:         'bg-gray-100 text-gray-700 border-gray-200',
};

type SortField = 'litigationNumber' | 'title' | 'taskClient' | 'stage' | 'forum' | 'status' | 'responsible' | 'nextDue' | 'lastActivity' | 'quoted' | 'billed' | 'collected' | 'outstandingAR';

// ─── Helpers ──────────────────────────────────────────────────────────────

function stageLabel(s: Stage | null): string {
  if (!s) return '';
  return s.replace(/_/g, ' ');
}

function lawAreaLabel(la: string): string {
  if (la === 'INCOME_TAX') return 'Income Tax';
  if (la === 'GST') return 'GST';
  if (la === 'OTHER') return 'Other';
  return la;
}

function daysBetween(a: Date, b: Date): number {
  const msPerDay = 86400000;
  return Math.floor((b.getTime() - a.getTime()) / msPerDay);
}

/** Return the earliest pending step due/hearing date, or null. */
function getNextDueDate(steps: Step[]): string | null {
  let earliest: string | null = null;
  for (const s of steps) {
    if (s.status !== 'PENDING') continue;
    const d = s.dueDate || s.hearingDate;
    if (!d) continue;
    if (!earliest || d < earliest) earliest = d;
  }
  return earliest;
}

/** Check if any PENDING step is overdue. */
function hasOverdueStep(steps: Step[], today: string): boolean {
  for (const s of steps) {
    if (s.status !== 'PENDING') continue;
    const d = s.dueDate || s.hearingDate;
    if (d && d < today) return true;
  }
  return false;
}

function fmtAmt(v: number | null | undefined): string {
  if (!v) return '—';
  return `₹${formatRupeesUI(v)}`;
}

// ─── Page ─────────────────────────────────────────────────────────────────

const LitigationsList: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<Litigation[]>([]);
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState<StaffLite[]>([]);

  // Filters
  const [statusFilter, setStatusFilter] = useState<'ALL' | LitStatus>('OPEN');
  const [stageFilter, setStageFilter] = useState<'ALL' | Stage>('ALL');
  const [lawAreaFilter, setLawAreaFilter] = useState<'ALL' | 'INCOME_TAX' | 'GST' | 'OTHER'>('ALL');
  const [assigneeFilter, setAssigneeFilter] = useState<'ALL' | string>('ALL');
  const [dormantMode, setDormantMode] = useState<'include' | 'exclude'>('include');
  const [search, setSearch] = useState('');

  // Stat card click filters
  const [needsAttention, setNeedsAttention] = useState(false);
  const [unbilledOnly, setUnbilledOnly] = useState(false);
  const [cardStatusFilter, setCardStatusFilter] = useState<'ALL' | 'OPEN' | 'CLOSED'>('ALL');

  // Sort
  const [sortField, setSortField] = useState<SortField>('nextDue');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const handleSort = (f: SortField) => {
    if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(f); setSortDir('asc'); }
  };
  const SI = ({ f }: { f: SortField }) =>
    sortField !== f ? <span className="ml-1 text-gray-300 text-xs">{'↕'}</span>
    : <span className="ml-1 text-blue-600 text-xs">{sortDir === 'asc' ? '↑' : '↓'}</span>;

  // Load staff once
  useEffect(() => {
    getStaff().then((r: any) => {
      const list: StaffLite[] = (r.data || r).filter((s: StaffLite) => s.isActive !== false);
      setStaff(list);
    }).catch(() => {});
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (statusFilter !== 'ALL') params.status = statusFilter;
      if (stageFilter !== 'ALL') params.stage = stageFilter;
      if (lawAreaFilter !== 'ALL') params.lawArea = lawAreaFilter;
      if (assigneeFilter !== 'ALL') params.assignee = assigneeFilter;
      if (dormantMode === 'exclude') params.dormant = 'exclude';
      if (search.trim()) params.search = search.trim();
      const r = await listAllLitigations(params);
      setItems(r.data);
    } catch (err: any) {
      showToast(err?.response?.data?.message ?? 'Failed to load litigations', 'error');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, stageFilter, lawAreaFilter, assigneeFilter, dormantMode, search]);

  // Refetch when any filter (except search) changes
  useEffect(() => { refresh(); }, [statusFilter, stageFilter, lawAreaFilter, assigneeFilter, dormantMode]); // eslint-disable-line

  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const todayDate = useMemo(() => new Date(), []);

  // Enrich with computed fields
  const enriched = useMemo(() => items.map((lit) => {
    const nextDue = getNextDueDate(lit.steps);
    const overdue = hasOverdueStep(lit.steps, todayStr);
    const isNeedingAttention = !lit.hasNextAction || overdue;

    // Next due: find earliest pending step for display
    const upcomingStep = lit.steps
      .filter((s) => s.status === 'PENDING' && (s.dueDate || s.hearingDate))
      .sort((a, b) => {
        const ad = a.dueDate || a.hearingDate || '';
        const bd = b.dueDate || b.hearingDate || '';
        return ad.localeCompare(bd);
      })[0] || null;

    // Limitation countdown
    let limitDaysLeft: number | null = null;
    if (lit.limitationDate && lit.status === 'OPEN') {
      limitDaysLeft = daysBetween(todayDate, new Date(lit.limitationDate));
    }

    // Next due days (for amber coloring)
    let nextDueDays: number | null = null;
    if (nextDue) {
      nextDueDays = daysBetween(todayDate, new Date(nextDue));
    }

    // Last activity days ago
    let lastActivityDaysAgo: number | null = null;
    if (lit.lastActivityAt) {
      lastActivityDaysAgo = daysBetween(new Date(lit.lastActivityAt), todayDate);
    }

    return {
      ...lit,
      nextDue,
      overdue,
      isNeedingAttention,
      upcomingStep,
      limitDaysLeft,
      nextDueDays,
      lastActivityDaysAgo,
    };
  }), [items, todayStr, todayDate]);

  // Apply card click filters
  const filtered = useMemo(() => {
    let list = enriched;
    if (cardStatusFilter === 'OPEN') {
      list = list.filter((l) => l.status === 'OPEN');
    } else if (cardStatusFilter === 'CLOSED') {
      list = list.filter((l) => CLOSED_STATUSES.includes(l.status));
    }
    if (needsAttention) {
      list = list.filter((l) => l.isNeedingAttention);
    }
    if (unbilledOnly) {
      list = list.filter((l) => l.fees.unbilledCompleted > 0);
    }
    return list;
  }, [enriched, cardStatusFilter, needsAttention, unbilledOnly]);

  // Sort
  const sorted = useMemo(() => [...filtered].sort((a, b) => {
    const numSort = (av: number, bv: number) => sortDir === 'asc' ? av - bv : bv - av;
    const strSort = (av: string, bv: string) => sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    switch (sortField) {
      case 'litigationNumber': return strSort(a.litigationNumber, b.litigationNumber);
      case 'title': return strSort(a.title, b.title);
      case 'taskClient': return strSort(
        `${a.task.taskId} ${a.task.client?.clientName || ''}`,
        `${b.task.taskId} ${b.task.client?.clientName || ''}`
      );
      case 'stage': return strSort(a.currentStage || '', b.currentStage || '');
      case 'forum': return strSort(a.forum || '', b.forum || '');
      case 'status': return strSort(a.status, b.status);
      case 'responsible': return strSort(a.responsible?.staffName || '', b.responsible?.staffName || '');
      case 'nextDue': return strSort(a.nextDue || '￿', b.nextDue || '￿');
      case 'lastActivity': return strSort(a.lastActivityAt || '', b.lastActivityAt || '');
      case 'quoted': return numSort(a.fees.quoted, b.fees.quoted);
      case 'billed': return numSort(a.fees.billed, b.fees.billed);
      case 'collected': return numSort(a.fees.collected, b.fees.collected);
      case 'outstandingAR': return numSort(a.fees.outstandingAR, b.fees.outstandingAR);
      default: return 0;
    }
  }), [filtered, sortField, sortDir]);

  // Summary totals (from all enriched items, not filtered)
  const totals = useMemo(() => {
    const open = enriched.filter((l) => l.status === 'OPEN').length;
    const closed = enriched.filter((l) => CLOSED_STATUSES.includes(l.status)).length;
    const attention = enriched.filter((l) => l.isNeedingAttention).length;
    const quoted = enriched.reduce((s, l) => s + l.fees.quoted, 0);
    const billed = enriched.reduce((s, l) => s + l.fees.billed, 0);
    const collected = enriched.reduce((s, l) => s + l.fees.collected, 0);
    const outstandingAR = enriched.reduce((s, l) => s + l.fees.outstandingAR, 0);
    const unbilled = enriched.filter((l) => l.fees.unbilledCompleted > 0).length;
    return { open, closed, attention, quoted, billed, collected, outstandingAR, unbilled };
  }, [enriched]);

  const openInTaskDetail = (l: Litigation) => {
    navigate(`/tasks?openTask=${l.task.id}&detailTab=litigations`);
  };

  // Card click handlers
  const handleCardClick = (card: string) => {
    if (card === 'open') {
      const next = cardStatusFilter === 'OPEN' ? 'ALL' : 'OPEN';
      setCardStatusFilter(next as any);
      if (next !== 'ALL') { setNeedsAttention(false); setUnbilledOnly(false); }
    } else if (card === 'closed') {
      const next = cardStatusFilter === 'CLOSED' ? 'ALL' : 'CLOSED';
      setCardStatusFilter(next as any);
      if (next !== 'ALL') { setNeedsAttention(false); setUnbilledOnly(false); }
    } else if (card === 'attention') {
      setNeedsAttention((v) => !v);
      if (!needsAttention) { setCardStatusFilter('ALL'); setUnbilledOnly(false); }
    } else if (card === 'unbilled') {
      setUnbilledOnly((v) => !v);
      if (!unbilledOnly) { setCardStatusFilter('ALL'); setNeedsAttention(false); }
    }
  };

  // Row background tint
  const rowTint = (l: typeof sorted[0]): string => {
    const classes: string[] = [];
    if (l.overdue) {
      classes.push('bg-red-50/60');
    } else if (l.nextDueDays !== null && l.nextDueDays >= 0 && l.nextDueDays <= 7) {
      classes.push('bg-amber-50/60');
    } else if (!l.hasNextAction && l.status === 'OPEN') {
      classes.push('bg-purple-50/60');
    }
    if (l.pausedReason) {
      classes.push('opacity-50');
    }
    return classes.join(' ');
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Litigations</h2>
          <p className="text-sm text-gray-500 mt-0.5">Every legal matter across the firm. Click a row to open its task and edit / add steps / raise invoices.</p>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
        <SummaryCard
          label="Open" value={String(totals.open)} tone="blue"
          active={cardStatusFilter === 'OPEN'} onClick={() => handleCardClick('open')}
        />
        <SummaryCard
          label="Closed" value={String(totals.closed)} tone="gray"
          active={cardStatusFilter === 'CLOSED'} onClick={() => handleCardClick('closed')}
        />
        <SummaryCard
          label="Needs Attention" value={String(totals.attention)} tone="red"
          active={needsAttention} onClick={() => handleCardClick('attention')}
        />
        <SummaryCard label="Quoted" value={fmtAmt(totals.quoted)} tone="indigo" />
        <SummaryCard label="Billed" value={fmtAmt(totals.billed)} tone="emerald" />
        <SummaryCard label="Collected" value={fmtAmt(totals.collected)} tone="emerald" />
        <SummaryCard label="O/s AR" value={fmtAmt(totals.outstandingAR)} tone="amber" />
        <SummaryCard
          label="Unbilled" value={String(totals.unbilled)} tone="purple"
          active={unbilledOnly} onClick={() => handleCardClick('unbilled')}
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-white border border-gray-200 rounded-lg px-3 py-2">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') refresh(); }}
          onBlur={() => refresh()}
          placeholder="Search title / case # / litigation #..."
          className="input-field text-sm py-1 w-56"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as any)}
          className="input-field text-sm py-1 w-40"
        >
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s === 'ALL' ? 'All statuses' : s.replace(/_/g, ' ')}</option>)}
        </select>
        <select
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value as any)}
          className="input-field text-sm py-1 w-40"
        >
          {STAGE_OPTIONS.map((s) => <option key={s} value={s}>{s === 'ALL' ? 'All stages' : s.replace(/_/g, ' ')}</option>)}
        </select>
        <select
          value={lawAreaFilter}
          onChange={(e) => setLawAreaFilter(e.target.value as any)}
          className="input-field text-sm py-1 w-36"
        >
          {LAW_AREA_OPTIONS.map((la) => <option key={la} value={la}>{la === 'ALL' ? 'All law areas' : lawAreaLabel(la)}</option>)}
        </select>
        <select
          value={assigneeFilter}
          onChange={(e) => setAssigneeFilter(e.target.value)}
          className="input-field text-sm py-1 w-40"
        >
          <option value="ALL">All assignees</option>
          {staff.map((s) => <option key={s.id} value={String(s.id)}>{s.staffName}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={dormantMode === 'exclude'}
            onChange={(e) => setDormantMode(e.target.checked ? 'exclude' : 'include')}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          Hide dormant
        </label>
        <span className="ml-auto text-xs text-gray-500">{sorted.length} litigation{sorted.length === 1 ? '' : 's'}</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="text-center py-10 text-gray-400 text-sm">Loading...</div>
        ) : sorted.length === 0 ? (
          <div className="text-center py-10 text-gray-400 text-sm">
            No litigations match the current filters. Add one from a task's <strong>Litigations</strong> tab.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="text-left px-3 py-2 cursor-pointer select-none whitespace-nowrap" onClick={() => handleSort('litigationNumber')}>Litigation <SI f="litigationNumber" /></th>
                  <th className="text-left px-3 py-2 cursor-pointer select-none whitespace-nowrap" onClick={() => handleSort('taskClient')}>Task + Client <SI f="taskClient" /></th>
                  <th className="text-left px-3 py-2 cursor-pointer select-none" onClick={() => handleSort('stage')}>Stage <SI f="stage" /></th>
                  <th className="text-left px-3 py-2 cursor-pointer select-none" onClick={() => handleSort('forum')}>Forum <SI f="forum" /></th>
                  <th className="text-left px-3 py-2 cursor-pointer select-none" onClick={() => handleSort('status')}>Status <SI f="status" /></th>
                  <th className="text-left px-3 py-2 cursor-pointer select-none" onClick={() => handleSort('responsible')}>Responsible <SI f="responsible" /></th>
                  <th className="text-left px-3 py-2 cursor-pointer select-none whitespace-nowrap" onClick={() => handleSort('nextDue')}>Next due <SI f="nextDue" /></th>
                  <th className="text-left px-3 py-2 cursor-pointer select-none whitespace-nowrap" onClick={() => handleSort('lastActivity')}>Last Activity <SI f="lastActivity" /></th>
                  <th className="text-right px-3 py-2 cursor-pointer select-none" onClick={() => handleSort('quoted')}>Quoted <SI f="quoted" /></th>
                  <th className="text-right px-3 py-2 cursor-pointer select-none" onClick={() => handleSort('billed')}>Billed <SI f="billed" /></th>
                  <th className="text-right px-3 py-2 cursor-pointer select-none" onClick={() => handleSort('collected')}>Collected <SI f="collected" /></th>
                  <th className="text-right px-3 py-2 cursor-pointer select-none whitespace-nowrap" onClick={() => handleSort('outstandingAR')}>O/s AR <SI f="outstandingAR" /></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((l) => (
                  <tr
                    key={l.id}
                    onClick={() => openInTaskDetail(l)}
                    className={`border-b border-gray-50 hover:bg-blue-50/40 cursor-pointer align-top ${rowTint(l)}`}
                  >
                    {/* Litigation */}
                    <td className="px-3 py-2">
                      <div className="font-mono text-xs text-purple-700 font-bold">{l.litigationNumber}</div>
                      <div className="text-gray-900 font-medium truncate max-w-[240px]" title={l.title}>{l.title}</div>
                      {l.caseNumber && <div className="text-[11px] text-gray-500 font-mono">Case: {l.caseNumber}</div>}
                    </td>
                    {/* Task + Client */}
                    <td className="px-3 py-2">
                      <div className="font-mono text-xs text-blue-700 font-medium">{l.task.taskId}</div>
                      <div className="text-gray-600 text-xs truncate max-w-[160px]" title={l.task.taskName}>{l.task.taskName}</div>
                      {l.task.client && <div className="text-[11px] text-gray-500 truncate max-w-[160px]" title={l.task.client.clientName}>{l.task.client.clientName}</div>}
                    </td>
                    {/* Stage */}
                    <td className="px-3 py-2">
                      {l.currentStage ? (
                        <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${STAGE_BADGE[l.currentStage]}`}>
                          {stageLabel(l.currentStage)}
                        </span>
                      ) : <span className="text-gray-300">{'—'}</span>}
                    </td>
                    {/* Forum */}
                    <td className="px-3 py-2 text-xs text-gray-600 truncate max-w-[140px]">{l.forum ?? '—'}</td>
                    {/* Status + limitation countdown */}
                    <td className="px-3 py-2">
                      <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${STATUS_BADGE[l.status]}`}>
                        {l.status.replace(/_/g, ' ')}
                      </span>
                      {l.limitDaysLeft !== null && l.status === 'OPEN' && (
                        <span className={`ml-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${
                          l.limitDaysLeft < 7 ? 'bg-red-100 text-red-700 border-red-200'
                          : l.limitDaysLeft < 15 ? 'bg-amber-100 text-amber-700 border-amber-200'
                          : 'bg-gray-100 text-gray-600 border-gray-200'
                        }`}>
                          Appeal: {l.limitDaysLeft}d left
                        </span>
                      )}
                    </td>
                    {/* Responsible */}
                    <td className="px-3 py-2 text-xs text-gray-700 truncate max-w-[120px]">
                      {l.responsible?.staffName ?? '—'}
                    </td>
                    {/* Next due */}
                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      {l.upcomingStep ? (
                        <>
                          <div className={
                            l.overdue ? 'text-red-600 font-medium'
                            : l.nextDueDays !== null && l.nextDueDays >= 0 && l.nextDueDays <= 7 ? 'text-amber-600 font-medium'
                            : 'text-gray-700'
                          }>
                            {formatDateDdMmmYy(l.upcomingStep.dueDate || l.upcomingStep.hearingDate || '')}
                          </div>
                          <div className="text-[11px] text-gray-500 truncate max-w-[140px]" title={l.upcomingStep.title}>
                            {l.upcomingStep.title}
                          </div>
                        </>
                      ) : !l.hasNextAction && l.status === 'OPEN' ? (
                        <span className="text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200">No next action</span>
                      ) : <span className="text-gray-300">{'—'}</span>}
                    </td>
                    {/* Last Activity */}
                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      {l.lastActivityAt ? (
                        <span className={l.lastActivityDaysAgo !== null && l.lastActivityDaysAgo > 60 ? 'text-red-600' : 'text-gray-500'}>
                          {formatDateDdMmmYy(l.lastActivityAt)}
                        </span>
                      ) : <span className="text-gray-300">{'—'}</span>}
                    </td>
                    {/* Quoted */}
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {l.fees.quoted > 0 ? fmtAmt(l.fees.quoted) : <span className="text-gray-300">{'—'}</span>}
                    </td>
                    {/* Billed */}
                    <td className="px-3 py-2 text-right whitespace-nowrap text-emerald-700">
                      {l.fees.billed > 0 ? fmtAmt(l.fees.billed) : <span className="text-gray-300">{'—'}</span>}
                    </td>
                    {/* Collected */}
                    <td className="px-3 py-2 text-right whitespace-nowrap text-emerald-700">
                      {l.fees.collected > 0 ? fmtAmt(l.fees.collected) : <span className="text-gray-300">{'—'}</span>}
                    </td>
                    {/* O/s AR */}
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {l.fees.outstandingAR > 0 ? (
                        <span className="text-amber-700 font-medium">{fmtAmt(l.fees.outstandingAR)}</span>
                      ) : <span className="text-gray-300">{'—'}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Summary card ─────────────────────────────────────────────────────────

const TONE_BG: Record<string, string> = {
  blue:    'bg-blue-50 border-blue-200 text-blue-700',
  gray:    'bg-gray-50 border-gray-200 text-gray-700',
  indigo:  'bg-indigo-50 border-indigo-200 text-indigo-700',
  emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  amber:   'bg-amber-50 border-amber-200 text-amber-700',
  red:     'bg-red-50 border-red-200 text-red-700',
  purple:  'bg-purple-50 border-purple-200 text-purple-700',
};

const SummaryCard: React.FC<{
  label: string;
  value: string;
  tone: keyof typeof TONE_BG;
  onClick?: () => void;
  active?: boolean;
}> = ({ label, value, tone, onClick, active }) => (
  <div
    className={`rounded-xl border p-3 ${TONE_BG[tone]} ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''} ${active ? 'ring-2 ring-offset-1 ring-blue-500' : ''}`}
    onClick={onClick}
  >
    <div className="text-[11px] uppercase tracking-wider opacity-80">{label}</div>
    <div className="text-xl font-bold mt-1">{value}</div>
  </div>
);

export default LitigationsList;
